import { Link } from "react-router-dom";
import { ArrowRight, Award, BadgeCheck, MapPin } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { MetricsBand, Manifesto, CTABand } from "@/components/home/HomeSections";
import { IMAGES } from "@/data/content";

const STANDARDS = [
  { icon: BadgeCheck, title: "A dedicated, professional team", desc: "Every project is handled by experienced professionals who take pride in getting the details right." },
  { icon: Award, title: "Security-first delivery", desc: "OWASP-aligned code review, dependency scanning and penetration-test-ready builds as standard." },
  { icon: MapPin, title: "UK-based & accountable", desc: "Based in Manchester, working your hours, under UK contracts and GDPR-compliant by default." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,102,255,0.15),transparent_60%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">About TechAxis</p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl" data-testid="about-page-title">
              We make technology <span className="text-gradient">simple, effective and accessible</span>
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              TechAxis Ltd. is a UK technology company helping startups, small businesses and established
              organisations turn ideas into reliable, user-friendly and scalable digital solutions. We exist
              because great engineering should not be reserved for enterprises with enterprise budgets.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Our team covers the full lifecycle — strategy, design, build, cloud, security and 24/7 support —
              so you get one accountable partner instead of a chain of vendors pointing at each other.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                data-testid="about-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white shadow-[0_0_25px_rgba(0,102,255,0.35)] transition-all hover:bg-cyan-400 hover:text-ink"
              >
                Meet the team <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="spotlight relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(3,7,18,0.7)]">
              <img src={IMAGES.team} alt="TechAxis engineers collaborating on system architecture" className="aspect-[16/11] w-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <MetricsBand />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="How we work"
          title="Standards we refuse to compromise"
          description="Quality, innovation, security and customer satisfaction are not marketing words here — they are how engagements are structured."
          testid="about-standards"
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {STANDARDS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="card-glow h-full rounded-2xl border border-white/10 bg-navy-900/60 p-7" data-testid={`about-standard-${i + 1}`}>
                <s.icon className="h-6 w-6 text-cyan-300" />
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Manifesto />
      <CTABand />
    </div>
  );
}
